import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Image from '@cms/image'

type CarouselProps = {
  images: Array<{
    src: string
    placeholder: string
    alt: string
    sizes?: string
    ratio?: number
  }>
}

const CarouselItem = ({
  src,
  placeholder,
  alt,
  sizes,
  ratio,
  idx,
  onItemClick
}: {
  src: string
  placeholder: string
  alt: string
  sizes?: string
  ratio?: number
  idx: number
  onItemClick?: (idx: number) => void
}) => {
  const onClick = useCallback(() => {
    console.log('click', idx)
    onItemClick && onItemClick(idx)
  }, [idx, onItemClick]) 

  return (
    <div
      className="carousel-item"
      onClick={onClick}
    >
        <Image
          src={src}
          placeholder={placeholder}
          alt={alt}
          ratio={ratio || 1}
          sizes={sizes || '100vw'}
        />
    </div>
  )
}

const Carousel = ({
  images,
}: CarouselProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const prevWidth = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current; 
    if (!el) return;

    const currentMinHeight = parseFloat(el.style.minHeight || '0');
    const currentHeight = el.clientHeight;

    if (currentHeight > currentMinHeight) {
      el.style.minHeight = `${currentHeight}px`;  
    }
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(([entry]) => {
      const newWidth = entry.contentRect.width;
      if (prevWidth.current !== null && prevWidth.current !== newWidth) {
        if (el.firstChild instanceof HTMLElement) {
          el.style.minHeight = el.firstChild.clientHeight + 'px';
        } else {
          el.style.minHeight = '0px';
        }
      }
      prevWidth.current = newWidth;
    });

    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div className='carousel'>
      <div className="carousel-current" ref={ref}>
        <Image
          src={images[currentIdx].src}
          placeholder={images[currentIdx].placeholder}
          alt={images[currentIdx].alt}
          ratio={images[currentIdx].ratio || 1}
          // Warning: 440px is hardcoded here, only works for publications page
          sizes="(min-width: 58rem) 440px, calc(100vw - 40px)"
        />
      </div>
      <div className="carousel-list">
        {images.map((image, index) => (
          <CarouselItem
            key={index}
            idx={index}
            src={image.src}
            placeholder={image.placeholder}
            alt={image.alt}
            sizes={image.sizes}
            ratio={1}
            onItemClick={setCurrentIdx}
          />
        ))}
      </div>
    </div>
  )
}

export default Carousel
